import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Sidebar from '../Components/Sidebar';
import IP_URL from '../Components/connect';
import { useSelector } from 'react-redux';
import { selectUser } from '../Redux/currentUserSlice';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BarIcon from '@mui/icons-material/BarChart';
import MeterIcon from '@mui/icons-material/GasMeter';
import AlertIcon from '@mui/icons-material/ReportGmailerrorred';

// -------------------- Styled Components --------------------
const Main = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: #f4f7fc;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px auto 100px;
  width: 95%;
  max-width: 1200px;
`;

const TopStats = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 150px;
  background-color: #0283e5;
  color: #fff;
  padding: 15px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;

  &:hover {
    background-color: #0261b0;
    transform: scale(1.05);
    box-shadow: 0 10px 20px rgba(0,0,0,0.3);
  }
`;

const StatLabel = styled.div`
  font-weight: bold;
  font-size: 16px;
`;

const StatValue = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-top: 5px;
  display: flex;
  align-items:center;
  justify-content: space-between;
`;

const ViewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const Section = styled.div`
  background-color: ${(props) => props.bg || '#fff'};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h3`
  margin-bottom: 15px;
  color: #0283e5;
`;

const ActivityItem = styled.div`
  padding: 12px;
  margin-bottom: 10px;
  background-color: #f9f9f9;
  border-radius: 10px;
  transition: 0.3s all ease;
  cursor: pointer;

  &:hover {
    background-color: #e0f2fb;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const ActivityText = styled.div`
  font-size: 14px;
  color: #333;
`;

// -------------------- Animated Wave Bar --------------------
const WavyBar = ({ x, y, width, height, payload }) => {
  const waveHeight = 25;
  const barId = `wave-${payload.day}`;
  const showWave = payload.consumption > 0;

  return (
    <g>
      <defs>
        {showWave && (
          <>
            <clipPath id={barId}>
              <rect x={x} y={y - waveHeight} width={width} height={waveHeight} />
            </clipPath>

            <pattern
              id={`wavePattern-${payload.day}`}
              x="0"
              y={y - waveHeight}
              width={width}
              height={waveHeight}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`
                  M0 ${waveHeight}
                  C ${width * 0.25} ${waveHeight - 10}, ${width * 0.75} ${waveHeight + 10}, ${width} ${waveHeight}
                  V ${waveHeight + 10} H0 Z
                `}
                fill="#7676f7"
              />
              <animate
                attributeName="x"
                from="0"
                to={`-${width}`}
                dur="2s"
                repeatCount="indefinite"
              />
            </pattern>
          </>
        )}
      </defs>

      <rect x={x} y={y} width={width} height={height} fill="#7676f7" />
      {showWave && <rect x={x} y={y - waveHeight} width={width} height={waveHeight} fill={`url(#wavePattern-${payload.day})`} clipPath={`url(#${barId})`} />}
    </g>
  );
};

// -------------------- Dashboard Component --------------------
const Dashboard = () => {
  const [consumptionData, setConsumptionData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [organizationData, setOrganizationData] = useState({});
  const [meterData, setMeterData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { token, uniqueKey } = useSelector(selectUser);
  const navigate = useNavigate();

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // -------------------- Fetch Data --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resConsumption = await fetch(`${IP_URL}/weeklyData/weeklyData/${uniqueKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dataConsumption = await resConsumption.json();
        const daysOfWeek = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
        const dayMap = {}; daysOfWeek.forEach(day => dayMap[day]=0);
        Object.values(dataConsumption).forEach(readings => readings.forEach(r => { dayMap[r.day] += r.consumption; }));
        setConsumptionData(daysOfWeek.map(day => ({ day, consumption: dayMap[day]/1000 })));

        const latestActivity = Object.entries(dataConsumption)
          .map(([meter, readings]) => {
            if(!readings || readings.length===0) return null;
            const lastEntry = readings.sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime))[0];
            return { meterId: meter,
               day:lastEntry.day,
                consumption:lastEntry.consumption,
                 date: formatLocalTime(lastEntry.dateTime) };
          }).filter(Boolean);
        setMeterData(latestActivity);

        const resOrg = await fetch(`${IP_URL}/organization/${uniqueKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const orgData = await resOrg.json();
        setOrganizationData(orgData);

        if(orgData?.meters){
          const matchedActivity = latestActivity.map(act => {
            const meter = orgData.meters.find(m=>m.meterId===act.meterId);
            return {...act, meterName: meter ? meter.meterName : 'Unknown Meter'};
          });
          setActivityData(matchedActivity);
        }
      } catch (err) {
        console.error(err);
        toast.error('Error fetching dashboard data');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [token, uniqueKey]);

  const totalConsumption = consumptionData.reduce((acc,c)=>acc+c.consumption,0).toFixed(3);

  // Converts UTC or ISO string to local time
 function formatLocalTime(isoString) {
  if (!isoString) return "N/A";

  // Ensure the string is treated as UTC
  const date = new Date(isoString.endsWith('Z') ? isoString : isoString + 'Z');

  const options = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // for AM/PM format
  };

  return date.toLocaleString(undefined, options);
}

  return (
    <Main>
      <Navbar showMenuButton toggleSidebar={toggleSidebar} />
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Container>
        {/* Top Stats Cards */}
        <TopStats>
          <StatCard onClick={()=>navigate('/meterData')}>
            <StatLabel>Meter Count</StatLabel>
            <StatValue><MeterIcon /> {organizationData.meters?.length || 0}</StatValue>
          </StatCard>

          <StatCard onClick={()=>navigate('/consumption-report')}>
            <StatLabel>Total Consumption</StatLabel>
            <StatValue><BarIcon /> {totalConsumption} m³</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Alerts</StatLabel>
            <StatValue><AlertIcon /> 0</StatValue>
          </StatCard>
        </TopStats>

        {/* Consumption Chart + Activity */}
        <ViewSection>
          <Section>
            <SectionTitle>Weekly Consumption</SectionTitle>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={consumptionData} margin={{ top: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="consumption" fill="#7676f7" shape={<WavyBar />} />
              </BarChart>
            </ResponsiveContainer>
          </Section>

          <Section bg="#f0f0f0">
            <SectionTitle>Recent Activity</SectionTitle>
            {loading ? <div>Loading...</div> : activityData.map((act, idx)=>(
              <ActivityItem key={idx} onClick={()=>navigate(`/meter-details/${act.meterId}`)}>
                <ActivityText><strong>Meter:</strong> {act.meterName}</ActivityText>
                <ActivityText><strong>Date:</strong> {act.date}</ActivityText>
                <ActivityText><strong>Consumption:</strong> {(act.consumption/1000).toFixed(3)} m³</ActivityText>
              </ActivityItem>
            ))}
          </Section>
        </ViewSection>
      </Container>

      <Footer />
      <ToastContainer position="top-right" autoClose={4000} />
    </Main>
  );
};

export default Dashboard;
